import type { TemplateVariable } from '../interfaces/template-variable.interface';
import { templateEditorLogger } from '../logger.instance';
import { SELECTORS, CLASSES } from '../utils/constants';
import { escapeHtml } from '../utils/escape-html';
import { getIconForType } from '../utils/icon-helper';
import type {
  BlockStatement,
  Expression,
  PathExpression,
  Program,
  Literal,
  SubExpression,
} from './ast-node.model';
import { MustacheStatementNode } from './mustache-statement';
import { PathExpressionNode } from './path-expression';
import { ProgramNode } from './program';
import { ArgumentParser } from './utils/argument-parser';
import { DomHelpers } from './utils/dom-helpers';

const logger = templateEditorLogger.extend('block-statement');

/**
 * Alias de tipo para a função que constrói uma AST a partir de um nó do DOM.
 */
type AstBuilder = (node: Node) => Program;
/**
 * Alias de tipo para a função que renderiza uma AST para HTML.
 */
type HtmlRenderer = (program: Program) => string;

/**
 * Implementação do nó `BlockStatement` da AST.
 * Representa helpers de bloco como `{{#if}}...{{/if}}` ou `{{#each}}...{{/each}}`.
 */
export class BlockStatementNode implements BlockStatement {
  public readonly type = 'BlockStatement';

  public constructor(
    public path: PathExpression,
    public params: Expression[],
    public program: Program,
    public inverse: Program | null,
  ) {}

  /**
   * Converte o nó de volta para a sintaxe de string do Handlebars.
   */
  public toString(): string {
    const paramsString = this.params.map((param) => param.toString()).join(' ');
    const pathString = `${this.path.toString()}${paramsString ? ' ' + paramsString : ''}`;
    logger.debug('toString', {
      pathString,
      params: this.params,
      program: this.program,
      inverse: this.inverse,
    });

    let result = `{{#${pathString}}}`;
    result += this.program.toString();
    if (this.inverse) {
      result += `{{else}}`;
      result += this.inverse.toString();
    }

    result += `{{/${this.path.toString()}}}`;
    logger.debug('toString result', { result });
    return result;
  }

  /**
   * Analisa um elemento de bloco do DOM para construir um `BlockStatementNode`.
   *
   * @param element O elemento `div.block-statement` do DOM.
   * @param astBuilder Função para construir recursivamente as ASTs dos blocos `program` e `inverse`.
   * @param knownVariables Mapa de variáveis para ajudar a interpretar os argumentos.
   * @returns Uma instância de `BlockStatementNode` ou `null` se o parsing falhar.
   */
  public static parse(
    element: HTMLElement,
    astBuilder: AstBuilder,
    knownVariables: Map<string, TemplateVariable>,
  ): BlockStatementNode | null {
    logger.debug('parse called', { element, knownVariables });
    if (!element.matches(SELECTORS.BLOCK_STATEMENT)) {
      logger.debug('parse: element does not match BLOCK_STATEMENT selector');
      return null;
    }

    const helperName = element.dataset['blockHelper'];
    if (!helperName) {
      logger.debug('parse: missing blockHelper dataset');
      return null;
    }

    const path = new PathExpressionNode(helperName);

    const header = element.querySelector(SELECTORS.BLOCK_HEADER) as HTMLElement | null;
    logger.debug('parse: found header', { header });
    const params = header ? ArgumentParser.parseArguments(header, astBuilder, knownVariables) : [];
    logger.debug('parse: parsed params', { params });

    const programDiv = element.querySelector<HTMLElement>(SELECTORS.BLOCK_PROGRAM);
    logger.debug('parse: found programDiv', { programDiv });
    const program = programDiv ? astBuilder(programDiv) : new ProgramNode([]);
    logger.debug('parse: parsed program', { program });

    const inverseDiv = element.querySelector<HTMLElement>(SELECTORS.BLOCK_INVERSE);
    logger.debug('parse: found inverseDiv', { inverseDiv });
    const inverse = inverseDiv ? astBuilder(inverseDiv) : null;
    logger.debug('parse: parsed inverse', { inverse });

    const node = new BlockStatementNode(path, params, program, inverse);
    logger.debug('parse: created BlockStatementNode', { node });
    return node;
  }

  /**
   * Cria o botão de exclusão para o bloco.
   */
  private static createDeleteButton(): HTMLElement {
    logger.debug('createDeleteButton called');
    return DomHelpers.createDeleteButton('block-statement__delete-button');
  }

  /**
   * Cria um fragmento de DOM para um `BlockStatement` a ser inserido no editor.
   *
   * @param block O nó da AST do bloco.
   * @param knownVariables Mapa de variáveis para obter definições de argumentos.
   * @returns Um `DocumentFragment` contendo a representação DOM do bloco.
   */
  public static toDomFragment(
    block: BlockStatement,
    knownVariables: Map<string, TemplateVariable>,
  ): DocumentFragment {
    logger.debug('toDomFragment called', { block, knownVariables });
    const fragment = document.createDocumentFragment();
    const wrapper = document.createElement('div');
    // A UX foi melhorada para que o bloco seja um "chip" não editável, permitindo exclusão consistente com Backspace.
    wrapper.className = `block-statement ${CLASSES.CHIP_ENTER}`;
    wrapper.dataset['blockHelper'] = block.path.original;
    wrapper.contentEditable = 'false';

    const { icon } = getIconForType('block-helper');

    // Cria o cabeçalho (ex: `{{#if condicao}}`)
    const header = document.createElement('div');
    header.className = CLASSES.BLOCK_HEADER;
    header.contentEditable = 'false';
    header.innerHTML = icon;
    header.append(document.createTextNode(`{{#${block.path.original}`));

    // Adiciona placeholders para os argumentos
    const definition = knownVariables.get(block.path.original);
    logger.debug('toDomFragment: definition', { definition });
    if (definition?.args && definition.args.length > 0) {
      header.append(document.createTextNode('\u00A0')); // &nbsp;
      for (const [index, arg] of definition.args.entries()) {
        if (index > 0) header.append(document.createTextNode(',\u00A0'));

        const argWrapper = document.createElement('span');
        argWrapper.className = `${CLASSES.ARG_WRAPPER} block-statement__arg-wrapper`;

        const argSpan = document.createElement('span');
        argSpan.className = `${CLASSES.ARG_PLACEHOLDER} block-statement__arg-placeholder`;
        argSpan.dataset['argName'] = arg.name;
        argSpan.contentEditable = 'true';

        const defaultValue = arg.defaultValue?.toString();
        argSpan.textContent = defaultValue ?? arg.name;
        if (!defaultValue) argSpan.classList.add(CLASSES.IS_PLACEHOLDER);

        const tooltip = document.createElement('span');
        tooltip.className = 'block-statement__tooltip template-tooltip';
        tooltip.textContent = arg.description;

        argWrapper.append(argSpan, tooltip);
        header.append(argWrapper);
        logger.debug('toDomFragment: added arg', { arg, argWrapper });
      }
    }

    header.append(document.createTextNode(`}}`));

    // Cria o corpo editável do bloco.
    const programDiv = document.createElement('div');
    programDiv.className = CLASSES.BLOCK_PROGRAM;
    programDiv.contentEditable = 'true';
    programDiv.innerHTML = '&#8203;'; // Espaço de largura zero para torná-lo focável.

    // Cria o rodapé (ex: `{{/if}}`).
    const footer = document.createElement('div');
    footer.className = 'block-statement__footer';
    footer.contentEditable = 'false';
    footer.textContent = `{{/${block.path.original}}}`;

    wrapper.append(header, programDiv, footer, this.createDeleteButton());
    fragment.append(wrapper);
    logger.debug('toDomFragment: fragment created', { fragment, wrapper });
    return fragment;
  }

  /**
   * Converte um `BlockStatement` da AST para sua representação em string HTML.
   *
   * @param block O nó da AST.
   * @param knownVariables Mapa de variáveis.
   * @param htmlRenderer Função para renderizar recursivamente os programas `program` e `inverse`.
   * @returns A string HTML representando o bloco.
   */
  public static toHtml(
    block: BlockStatement,
    knownVariables: Map<string, TemplateVariable>,
    htmlRenderer: HtmlRenderer,
  ): string {
    logger.debug('toHtml called', { block, knownVariables });
    const helperName = escapeHtml(block.path.original);
    const { icon } = getIconForType('block-helper');

    const deleteButtonHtml = DomHelpers.renderDeleteButtonHtml('block-statement__delete-button');

    // O container principal do bloco agora é não-editável (`contenteditable="false"`) para se comportar como um "chip".
    let html = `<div class="${CLASSES.BLOCK_STATEMENT}" data-block-helper="${helperName}" contenteditable="false">`;

    let headerContent = `${icon}{{#${helperName}`;
    const definition = knownVariables.get(block.path.original);
    logger.debug('toHtml: definition', { definition });
    if (definition?.args && definition.args.length > 0) {
      const argsHtml = definition.args
        .map((arg, index) => {
          const param = block.params[index];
          let isPlaceholder = false;
          let valueHtml: string;

          if (param?.type.endsWith('Literal')) {
            valueHtml = escapeHtml(String((param as Literal).value));
          } else if (param?.type === 'PathExpression') {
            const variableDef = knownVariables.get((param as PathExpression).original);
            if (variableDef) {
              valueHtml = MustacheStatementNode.toHtml(
                new MustacheStatementNode(param as PathExpression, []),
                knownVariables,
              );
            } else {
              valueHtml = escapeHtml((param as PathExpression).original);
            }
          } else if (param?.type === 'SubExpression') {
            valueHtml = MustacheStatementNode.toHtml(
              {
                type: 'MustacheStatement',
                path: (param as SubExpression).path,
                params: (param as SubExpression).params,
                escaped: true,
                toString: param.toString.bind(param),
              },
              knownVariables,
            );
          } else {
            valueHtml = escapeHtml(arg.defaultValue?.toString() ?? arg.name);
            if (!arg.defaultValue) isPlaceholder = true;
          }

          const argName = escapeHtml(arg.name);
          const argDesc = escapeHtml(arg.description);
          const customClasses = isPlaceholder ? CLASSES.IS_PLACEHOLDER : '';

          logger.debug('toHtml: arg', { arg, param, valueHtml, isPlaceholder });

          return (
            `<span class="${CLASSES.ARG_WRAPPER} block-statement__arg-wrapper">` +
            `<span class="${CLASSES.ARG_PLACEHOLDER} block-statement__arg-placeholder ${customClasses}" data-arg-name="${argName}" contenteditable="true">${valueHtml}</span>` +
            `<span class="block-statement__tooltip template-tooltip">${argDesc}</span>` +
            `</span>`
          );
        })
        .join('&nbsp;');
      headerContent += ` ${argsHtml}`;
    }

    headerContent += `}}`;
    html += `<div class="${CLASSES.BLOCK_HEADER}" contenteditable="false">${headerContent}</div>`;

    // Corpo do `program` (bloco principal) é editável.
    html += `<div class="${CLASSES.BLOCK_PROGRAM}" contenteditable="true">${htmlRenderer(block.program)}</div>`;

    if (block.inverse) {
      html += `<div class="${CLASSES.BLOCK_INVERSE}-separator" contenteditable="false">{{else}}</div>`;
      // Corpo do `inverse` (bloco `else`) é editável.
      html += `<div class="${CLASSES.BLOCK_INVERSE}" contenteditable="true">${htmlRenderer(block.inverse)}</div>`;
    }

    html += `<div class="block-statement__footer" contenteditable="false">{{/${helperName}}}</div>`;
    html += deleteButtonHtml;
    html += '</div>';
    logger.debug('toHtml: html generated', { html });
    return html;
  }
}
